import { FC, useEffect, useState } from 'react';
import { captureException } from '@sentry/react';
import { Button, Col, Divider, Modal, Row } from 'antd';
import { TbArrowRight, TbArrowUpRight, TbCircleArrowUp, TbCloudCheck, TbInfoCircle, TbMist } from 'react-icons/tb';
import { useNavigate } from 'react-router-dom';

import { authorizedAxiosInstance } from '@/clients';
import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import { useEntitlements } from '@/hooks/useEntitlements';
import { useSearchCompanySyncProgress } from '@/layouts/02-Authenticated/layout/03-Content/routes/04-Search/searchCompanySyncProgressState';
import { PlanTag, PlatformAlert } from '@/shared';
import { toast } from '@/shared/components/toasts/PlatformToast';
import { useConnectedAccountStore } from '@/stores';
import { ConnectedAccount, ConnectedAccountType, SetStateMethod } from '@/types';
import { centerComponentInMiddleofView } from '@/utilities';

import BulkSelect from './layout/01-BulkSelect';
import DataSources from './layout/02-ConnectServices';
import Options from './layout/03-Options';

interface SyncToDataSourcesModalProps {
    open: boolean;
    setOpen: SetStateMethod<boolean>;
    results: any;
    resultsSelected: Set<string>;
    setCheckAll?: SetStateMethod<boolean>;
    setIndeterminate?: SetStateMethod<boolean>;
    setResultsSelected: SetStateMethod<Set<string>>;
}

const SyncToDataSourcesModal: FC<SyncToDataSourcesModalProps> = ({
    open,
    setOpen,
    results,
    resultsSelected,
    setCheckAll,
    setIndeterminate,
    setResultsSelected,
}): JSX.Element => {
    const [loading, setLoading] = useState(true);

    const { token } = useAntDesignTheme();
    const { trackQueuedSyncs } = useSearchCompanySyncProgress();

    const plainOptions = [
        // 'Company Overview',
        'Balance Sheet',
        'Income Statement',
        'Statement of Cash Flows',
        'Revenue by Product & Geography',
        'Key Performance Metrics',
    ];

    const defaultCheckedList = ['Balance Sheet', 'Income Statement', 'Statement of Cash Flows'];

    const [checkedList, setCheckedList] = useState<string[]>(defaultCheckedList);

    const onChange = (checked: boolean, option: string) => {
        setCheckedList((prev) => (checked ? [...prev, option] : prev.filter((item) => item !== option)));
    };

    const [filteredResults, setFilteredResults] = useState(() =>
        resultsSelected?.size !== 0
            ? results.filter((result: any) => resultsSelected.has(result?.entityId ?? result?.symbol))
            : [],
    );

    useEffect(() => {
        setLoading(false);

        const updatedFiltered = results.filter((result: any) =>
            resultsSelected.has(result?.entityId ?? result?.symbol),
        );

        setFilteredResults(updatedFiltered);
    }, [results, resultsSelected]);

    const [selectedConnectedAccounts, setSelectedConnectedAccounts] = useState<any[]>([]);

    const [selectedNotionDatabaseId, setSelectedNotionDatabaseId] = useState('');

    const { hasMinimumPlan } = useEntitlements();
    const canSync = hasMinimumPlan('plus');

    // !IMPORTANT: We might also add Notion here in the future (as the integration is mostly built out)
    const { connectedAccounts, fetchConnectedAccounts, loading: connectedAccountsLoading } = useConnectedAccountStore();

    useEffect(() => {
        if (open && connectedAccounts.length === 0) {
            void fetchConnectedAccounts();
        }
    }, [connectedAccounts.length, fetchConnectedAccounts, open]);

    const [segmented, setSegmented] = useState('annual');
    const [accountingFormat, setAccountingFormat] = useState('asReported');

    const navigate = useNavigate();

    // Handle Sync Logic

    const handleSync = async () => {
        try {
            setLoading(true);

            const payload = {
                connectedAccountsIds: selectedConnectedAccounts?.map((ca: ConnectedAccount) => ca?.id),
                databaseId: selectedNotionDatabaseId,
                tickerSymbols: filteredResults?.map((fr: any) => fr?.symbol),
                // Spreadsheet-Specific
                reports: checkedList,
                delineation: segmented,
                accountingFormat: accountingFormat,
            };

            const response = await authorizedAxiosInstance.put(`/api/connected-accounts/sync`, payload);
            const companyCount = filteredResults.length;
            const queuedSyncs = (response.data?.results?.googleSheets ?? []) as Array<{
                queued?: number;
                statusUrl?: string;
                syncRequestId?: string;
            }>;

            setOpen(false);
            setResultsSelected(new Set());
            setCheckAll?.(false);
            setIndeterminate?.(false);

            if (response.status === 202) {
                const queued = queuedSyncs.reduce((total, sync) => total + Number(sync.queued ?? 0), 0);

                toast('Sync queued', {
                    icon: <TbCloudCheck size='1rem' />,
                    description: `${queued.toLocaleString()} compan${queued === 1 ? 'y' : 'ies'} queued for background sync.`,
                });

                trackQueuedSyncs(queuedSyncs, companyCount);
            } else {
                toast('Sync completed', {
                    icon: <TbCloudCheck size='1rem' />,
                    description: `${companyCount.toLocaleString()} compan${companyCount === 1 ? 'y' : 'ies'} synced.`,
                });
            }
        } catch (error) {
            captureException(error);
            toast.error('Unable to start sync', {
                description: 'Please try again. No additional sync work was queued.',
            });
        } finally {
            setLoading(false);
        }
    };

    const disabled =
        loading ||
        filteredResults?.length === 0 ||
        selectedConnectedAccounts?.length === 0 ||
        checkedList?.length === 0 ||
        !canSync;

    const connectedAccountssIncludeGoogleSheets = selectedConnectedAccounts?.filter(
        (ca: ConnectedAccount) => ca?.type === ConnectedAccountType.GOOGLE_SHEETS,
    );

    return (
        <Modal
            destroyOnHidden
            onCancel={() => setOpen(false)}
            open={open}
            mask={false}
            title={
                <>
                    Sync to Data Sources
                    <PlanTag plan='plus' style={{ marginLeft: '0.5rem' }} />
                </>
            }
            style={{ minWidth: '60rem' }}
            bodyStyle={{ paddingTop: '1rem' }}
            footer={
                <Row gutter={[12, 0]} align={'middle'}>
                    <Col flex='auto' style={{ textAlign: 'left' }}>
                        <small>
                            <TbInfoCircle
                                style={{
                                    verticalAlign: '-0.1rem',
                                    marginRight: '0.25rem',
                                    color: token.colorTextTertiary,
                                }}
                            />
                            <span style={{ color: token.colorTextTertiary }}>
                                Learn more about syncing data to data sources in
                            </span>{' '}
                            <a href='https://docs.countercyclical.io/' target='_blank' rel='noopener noreferrer'>
                                our docs
                                <TbArrowUpRight style={{ verticalAlign: '-0.125rem', marginLeft: '0.125rem' }} />
                            </a>
                        </small>
                    </Col>
                    <Col flex='none'>
                        <Button onClick={() => setOpen(false)}>Cancel</Button>
                    </Col>
                    <Col flex='none'>
                        <Button type='primary' onClick={handleSync} loading={loading} disabled={disabled}>
                            Sync
                            <TbArrowRight size='1rem' style={{ verticalAlign: '-0.175rem' }} />
                        </Button>
                    </Col>
                </Row>
            }
        >
            <PlatformAlert
                status='info'
                title='Background sync'
                description='Fundamental data is synced in durable, rate-limit-aware background batches. You can close this modal after the sync is queued.'
            />
            {!canSync && (
                <PlatformAlert
                    showIcon
                    icon={<TbMist style={{ marginTop: '0.25rem' }} />}
                    status='info'
                    message='Model Syncing is limited by your current plan'
                    description='Upgrade your plan for additional options.'
                    style={{ marginTop: '1rem' }}
                    action={
                        <Button
                            type='primary'
                            style={{ marginTop: '0.625rem' }}
                            onClick={() => {
                                navigate(`/settings/account/plans`);
                            }}
                        >
                            <TbCircleArrowUp
                                size='1rem'
                                style={{ verticalAlign: '-0.175rem', marginRight: '0.375rem' }}
                            />
                            Upgrade Plan
                        </Button>
                    }
                />
            )}
            <Divider style={{ marginBottom: '0.5rem', marginTop: '0.5rem' }} />
            <Row wrap={false} align='stretch' style={{ minHeight: '24rem' }}>
                <Col span={11} style={{ display: 'flex' }}>
                    <BulkSelect
                        resultsSelected={resultsSelected}
                        setResultsSelected={setResultsSelected}
                        filteredResults={filteredResults}
                        setFilteredResults={setFilteredResults}
                    />
                </Col>
                <Col span={2} style={{ display: 'flex' }}>
                    <div style={{ flex: 1, ...centerComponentInMiddleofView }}>
                        <TbArrowRight size='1rem' style={{ color: token.colorTextTertiary }} />
                    </div>
                </Col>
                <Col span={11} style={{ display: 'flex' }}>
                    <DataSources
                        connectedAccounts={connectedAccounts}
                        loadingConnectedAccounts={connectedAccountsLoading}
                        selectedConnectedAccounts={selectedConnectedAccounts}
                        setSelectedConnectedAccounts={setSelectedConnectedAccounts}
                        setSelectedNotionDatabaseId={setSelectedNotionDatabaseId}
                    />
                </Col>
            </Row>
            {connectedAccountssIncludeGoogleSheets?.length !== 0 && (
                <>
                    <Divider style={{ marginBottom: '0.5rem', marginTop: '0.5rem' }} />
                    <Options
                        segmented={segmented}
                        setSegmented={setSegmented}
                        accountingFormat={accountingFormat}
                        setAccountingFormat={setAccountingFormat}
                        plainOptions={plainOptions}
                        checkedList={checkedList}
                        onChange={onChange}
                    />
                </>
            )}
        </Modal>
    );
};

export default SyncToDataSourcesModal;
